import {
  Edit as EditIcon,
  Close as CloseIcon,
  Check as SaveIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Switch,
  TextField,
  Grid,
  Stack,
  Alert,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  ClickAwayListener,
  Box,
} from "@mui/material";
import * as strings from "common/strings";
import Component from "components/Component";
import RawConfigEditor from "components/admin/RawConfigEditor";
import DefaultLoader from "components/common/DefaultLoader";
import EditableTextField from "components/common/EditableTextField";
import InlineSearch from "components/common/InlineSearch";
import ShortcutListener from "components/common/ShortcutListener";
import IndustryClassExposureLinkerDialog from "components/settings/IndustryClassExposureLinkerDialog";
import * as config from "config";
import _ from "lodash";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import * as claimsService from "services/claimsService";
import * as staticDataService from "services/staticDataService";
import { selectImpersonatedCompanyId } from "store/impersonationSlice";
import * as utils from "utils";

const IndustryClassTableRow = ({
  industryClass,
  editing,
  isEditing,
  setEditing,
  updateConfig,
  isUpdating,
  setLinking,
}) => {
  const saveRequired = isEditing
    ? !_.isEqual(
        {
          name: industryClass?.name,
          isDeactivated: industryClass?.isDeactivated,
        },
        {
          name: editing?.name,
          isDeactivated: editing?.isDeactivated,
        }
      )
    : false;

  const ConditionalClickAwayListener = ({
    listening,
    onClickAway,
    children,
  }) => {
    return listening ? (
      <ClickAwayListener onClickAway={onClickAway}>
        {children}
      </ClickAwayListener>
    ) : (
      <>{children}</>
    );
  };

  return (
    <ConditionalClickAwayListener
      key={industryClass?.key}
      listening={isEditing}
      onClickAway={() => {
        if (!saveRequired) {
          setEditing({});
        }
      }}
    >
      <TableRow
        sx={{
          "&:hover .edit-icon": {
            visibility: "visible",
          },
        }}
        hover
        onClick={() => {
          if (!isEditing) {
            setEditing({
              name: industryClass?.name,
              key: industryClass?.key,
              isDeactivated: industryClass?.isDeactivated,
            });
          }
        }}
      >
        <TableCell>
          {isEditing ? (
            <EditableTextField
              initialValue={editing?.name || ""}
              align={"left"}
              variant={"filled"}
              size={"small"}
              disabled={isUpdating}
              hiddenLabel
              autoFocus
              onSave={(value) => {
                setEditing((edit) => ({
                  ...edit,
                  name: value,
                }));
              }}
            />
          ) : (
            <Typography>{industryClass?.name}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant={"caption"} sx={{ color: "grey" }}>
            {industryClass?.key}
          </Typography>
        </TableCell>
        <TableCell align={"center"}>
          <Switch
            checked={
              !(isEditing
                ? editing?.isDeactivated
                : industryClass?.isDeactivated)
            }
            onChange={(e) => {
              e.preventDefault();
              setEditing((edit) => ({
                ...edit,
                isDeactivated: !e?.target?.checked,
              }));
            }}
            disabled={!isEditing || isUpdating}
          />
        </TableCell>
        <TableCell align={"center"}>
          <Tooltip title={"Link to Exposures"}>
            <IconButton
              disabled={isUpdating}
              onClick={(e) => {
                e.stopPropagation();
                setLinking(industryClass);
              }}
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          {isEditing ? (
            <Stack direction={"row"} justifyContent={"flex-end"}>
              {isUpdating ? (
                <DefaultLoader size={20} style={{ marginRight: "10px" }} />
              ) : (
                <>
                  <Tooltip title={"Save changes"}>
                    <IconButton
                      onClick={(e) => {
                        if (saveRequired) {
                          updateConfig([
                            {
                              name: editing?.name,
                              key: editing?.key,
                              isDeactivated: editing?.isDeactivated,
                            },
                          ]);
                          setEditing((edit) => ({
                            ...edit,
                            updating: true,
                          }));
                        } else {
                          setEditing({});
                        }
                        e.stopPropagation();
                      }}
                      color={saveRequired ? "primary" : "secondary"}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>
                  <ShortcutListener
                    shortcut={"escape"}
                    singleKey
                    action={() => {
                      setEditing({});
                    }}
                  />
                  <ShortcutListener
                    shortcut={"tab"}
                    singleKey
                    action={() => {
                      setEditing({});
                    }}
                  />
                  <Tooltip title={"Cancel"}>
                    <IconButton
                      onClick={(e) => {
                        setEditing({});
                        e.stopPropagation();
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          ) : (
            <IconButton
              className={"edit-icon"}
              sx={{
                visibility: "hidden",
              }}
              disabled={isUpdating}
            >
              <EditIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
    </ConditionalClickAwayListener>
  );
};

const IndustryClassTable = ({
  industryClasses,
  updateConfig,
  isUpdating,
  setLinking,
}) => {
  const [editing, setEditing] = useState({});

  useEffect(() => {
    if (!isUpdating) {
      setEditing({});
    }
  }, [isUpdating]);

  return (
    <TableContainer
      style={{
        height: "70vh",
      }}
    >
      <Table stickyHeader size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell align={"left"} width={"30%"}>
              {"Label"}
            </TableCell>
            <TableCell align={"left"} width={"30%"}>
              {"Key"}
            </TableCell>
            <TableCell align={"center"} width={"10%"}>
              {"Active"}
            </TableCell>
            <TableCell align={"center"} width={"20%"}>
              {"Linked Exposures"}
            </TableCell>
            <TableCell align={"right"} width={"5%"} />
          </TableRow>
        </TableHead>
        <TableBody>
          {(industryClasses || [])?.map((industryClass) => (
            <IndustryClassTableRow
              key={industryClass?.key}
              industryClass={industryClass}
              editing={editing}
              isEditing={editing?.key === industryClass?.key}
              setEditing={setEditing}
              updateConfig={updateConfig}
              isUpdating={
                editing?.updating && editing?.key === industryClass?.key
              }
              setLinking={setLinking}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const CreateIndustryClassDialog = ({
  open,
  close,
  industryClassKeys,
  updateConfig,
  isUpdating,
}) => {
  const [industryClasses, setIndustryClasses] = useState([
    {
      name: "",
      key: "",
      isDeactivated: false,
    },
  ]);

  const [loading, setLoading] = useState(false);

  const industryClassNames = open && industryClasses?.map((ic) => ic?.name);

  useEffect(() => {
    setIndustryClasses((currentIndustryClasses) =>
      currentIndustryClasses?.map((industryClass, index) => {
        const { id, alone } = strings.toUniqueId({
          value: industryClass?.name,
          list: [
            "ALL",
            ...industryClassKeys,
            ...(index > 0
              ? currentIndustryClasses?.map((ic) => ic?.key)?.slice(0, index)
              : []),
          ],
          verbose: true,
        });
        return {
          ...industryClass,
          key: id,
          unique: alone,
        };
      })
    );
  }, [industryClassNames, industryClassKeys]);

  useEffect(() => {
    if (!isUpdating && loading) {
      close();
    }
  }, [isUpdating, close, loading]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setIndustryClasses([
        {
          name: "",
          key: "",
          isDeactivated: false,
        },
      ]);
    }
  }, [open]);

  const isValid = industryClasses?.reduce((valid, industryClass) => {
    if (industryClass?.name?.length < 3 && industryClass?.name?.length > 0) {
      return false;
    }
    return valid;
  }, true);

  return (
    <Dialog
      open={open}
      onClose={close}
      PaperProps={{
        sx: {
          top: "20vh",
          width: "800px",
          maxWidth: "90vw",
          margin: "auto",
          position: "absolute",
        },
      }}
    >
      <DialogTitle>
        <Stack
          justifyContent={"space-between"}
          direction={"row"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>{"New Industry Class"}</Typography>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TableContainer
            style={{
              maxHeight: 600,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align={"left"} width={"40%"}>
                    {"Label"}
                  </TableCell>
                  <TableCell align={"left"}>{"Key"}</TableCell>
                  <TableCell align={"center"}>{"Active"}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody sx={{ background: "white" }}>
                {industryClasses?.map((industryClass, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <TextField
                          hiddenLabel
                          autoFocus
                          value={industryClass?.name}
                          align={"left"}
                          variant={"filled"}
                          size={"small"}
                          sx={{ width: "100%" }}
                          helperText={
                            (!industryClass?.unique ||
                              (industryClass?.name?.length < 3 &&
                                industryClass?.name?.length > 0)) && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  top: "8px",
                                  position: "relative",
                                }}
                              >
                                <InfoIcon
                                  fontSize={"small"}
                                  color={
                                    !industryClass?.unique ? "info" : "error"
                                  }
                                />
                                <span>
                                  {!industryClass?.unique
                                    ? "A similar industry class exists already."
                                    : "Industry class label too short"}
                                </span>
                              </span>
                            )
                          }
                          onChange={(e) => {
                            setIndustryClasses(
                              industryClasses?.map((ic, i) => {
                                if (i === index) {
                                  return {
                                    ...ic,
                                    name: e.target.value,
                                  };
                                }
                                return ic;
                              })
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <TextField
                          hiddenLabel
                          value={industryClass?.key}
                          align={"left"}
                          variant={"filled"}
                          size={"small"}
                          sx={{ width: "100%" }}
                          disabled
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }} align={"center"}>
                        <Switch
                          checked={!industryClass?.isDeactivated}
                          sx={{ marginTop: "4px" }}
                          onChange={(e) => {
                            setIndustryClasses(
                              industryClasses?.map((ic, i) => {
                                if (i === index) {
                                  return {
                                    ...ic,
                                    isDeactivated: !e?.target?.checked,
                                  };
                                }
                                return ic;
                              })
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {industryClasses?.length > 1 ? (
                          <IconButton
                            sx={{ marginTop: "4px" }}
                            onClick={() => {
                              setIndustryClasses((currentIndustryClasses) =>
                                currentIndustryClasses?.filter(
                                  (ic, i) => i !== index
                                )
                              );
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          <div style={{ width: "40px" }}></div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Button
              sx={{
                color: "lightslategray",
              }}
              variant={"outlined"}
              disableElevation
              color={"secondary"}
              onClick={() => {
                setIndustryClasses((currentIndustryClasses) => [
                  ...currentIndustryClasses,
                  {
                    name: "",
                    key: "",
                    isDeactivated: false,
                  },
                ]);
              }}
            >
              {"Add row"}
            </Button>
            <Button
              variant={"contained"}
              disableElevation
              color={"primary"}
              disabled={!isValid || loading}
              sx={{ height: "40px" }}
              onClick={() => {
                updateConfig(
                  industryClasses
                    ?.filter((ic) => ic?.key?.length > 3)
                    ?.map((ic) => ({
                      key: ic?.key,
                      name: ic?.name,
                      isDeactivated: ic?.isDeactivated,
                    }))
                );
                setLoading(true);
              }}
            >
              {loading ? <DefaultLoader size={20} /> : "Create"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const IndustryClassConfig = () => {
  const [industryClasses, setIndustryClasses] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [saved, setSaved] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linking, setLinking] = useState(null);
  const impersonatedCompanyId = useSelector(selectImpersonatedCompanyId);

  const {
    data: retrievedIndustryClasses,
    isLoading,
    refetch,
  } = claimsService.useIndustryClassesQuery();

  const [
    triggerUpdateConfig,
    { isLoading: isUpdating, isError },
  ] = staticDataService.useAdminUpdateConfigMutation();

  useEffect(() => {
    if (retrievedIndustryClasses != null) {
      setIndustryClasses(retrievedIndustryClasses);
    }
  }, [retrievedIndustryClasses]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const updateConfig = (values) => {
    const updatedIndustryClasses = [
      ...industryClasses?.map((industryClass) => {
        const updatedIndustryClass = values.find(
          (value) => value?.key === industryClass?.key
        );
        if (updatedIndustryClass) {
          values.splice(values.indexOf(updatedIndustryClass), 1);
          return updatedIndustryClass;
        }
        return industryClass;
      }),
      ...values,
    ];
    triggerUpdateConfig({
      key: "classifications",
      value: {
        industryClasses: updatedIndustryClasses,
      },
    }).then((resp) => {
      setIndustryClasses(resp?.data?.industryClasses);
      clearTimeout(saved);
      setSaved(
        setTimeout(() => {
          setSaved(false);
        }, 5000)
      );
    });
  };

  const pollForChanges = useCallback(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.claims("exposure/industry-classes"),
      { method: "get" },
      impersonatedCompanyId
    );
    return await response.json();
  }, [impersonatedCompanyId]);

  const searchedIndustryClasses = (
    industryClasses || []
  )?.filter((industryClass) =>
    industryClass?.name?.toLowerCase()?.includes(searchText)
  );

  return (
    <Component title={"Edit Industry Classes"}>
      <RawConfigEditor
        superUserOnly
        data={retrievedIndustryClasses}
        onSave={async (parsedJson) => {
          const resp = await triggerUpdateConfig({
            key: "classifications",
            value: { industryClasses: parsedJson },
          });
          setIndustryClasses(resp?.data?.industryClasses);
        }}
        isSaving={isUpdating}
        pollForChanges={pollForChanges}
        header={
          <InlineSearch
            placeholder={"Quick Search"}
            onChange={(text) => setSearchText(text.toLowerCase())}
          />
        }
        actions={
          <Button
            variant={"contained"}
            disableElevation
            color={"primary"}
            onClick={() => setCreating(true)}
            startIcon={<AddIcon />}
          >
            {"Add"}
          </Button>
        }
      >
        <Stack spacing={2}>
          {searchedIndustryClasses?.length > 0 ? (
            <IndustryClassTable
              industryClasses={searchedIndustryClasses}
              updateConfig={updateConfig}
              isUpdating={isUpdating}
              setLinking={setLinking}
            />
          ) : (
            <Box
              sx={{
                height: "70vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isLoading || !industryClasses ? (
                <DefaultLoader size={64} />
              ) : (
                <Typography>{`No matches found for '${searchText}'`}</Typography>
              )}
            </Box>
          )}
        </Stack>
      </RawConfigEditor>
      <CreateIndustryClassDialog
        open={creating}
        close={() => setCreating(false)}
        industryClassKeys={industryClasses?.map((ic) => ic?.key) || []}
        updateConfig={updateConfig}
        isUpdating={isUpdating}
      />
      <IndustryClassExposureLinkerDialog
        open={!!linking}
        close={() => {
          setLinking(null);
        }}
        selectedIndustryClass={linking}
      />
      <Grid xs={12} item>
        {isError ? (
          <Alert severity={"error"}>
            {
              "An error has occured when saving, please contact support at: support@cactus.bm"
            }
          </Alert>
        ) : saved !== false ? (
          <Alert severity={"success"}>
            {
              "Industry classes updated, we will be in touch should further details be required."
            }
          </Alert>
        ) : (
          <></>
        )}
      </Grid>
    </Component>
  );
};

export default IndustryClassConfig;
